import { jsx, jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import PropTypes from "prop-types";
import { useId } from "@reach/auto-id";
import "lodash/fp";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { Button } from "../button/button.es.js";
import { Dialog } from "../dialog/dialog.es.js";
import { Icon } from "../icon/icon.es.js";
import "../../legacy/components/content/content.es.js";
import "../../legacy/components/content/content-title.es.js";
import { Inline } from "../../legacy/components/inline/inline.es.js";
import "../../legacy/components/toolbar/toolbar.es.js";
import "../../legacy/components/toolbar/toolbar-background.es.js";
import "../../legacy/components/toolbar/toolbar-group.es.js";
import "../../legacy/components/toolbar/toolbar-group-left.es.js";
import "../../legacy/components/toolbar/toolbar-group-right.es.js";
import "../../legacy/components/toolbar/toolbar-item.es.js";
import "../../legacy/components/toolbar/toolbar-separator.es.js";
import { Overlay } from "../overlay/overlay.es.js";
import { ConfirmationDialogInner, ConfirmationDialogIcon, ConfirmationDialogHeader, ConfirmationDialogBody, ConfirmationDialogFooter } from "./confirmation-dialog.styles.es.js";
const propTypes = {
  animation: PropTypes.bool,
  "aria-label": (props, propName, componentName) => {
    const { title } = props;
    if (title === void 0) {
      return new Error(
        // eslint-disable-next-line max-len
        `The prop \`${propName}\` is marked as required in \`${componentName}\` when the prop \`title\` is not provided.`
      );
    }
    return null;
  },
  cancelText: PropTypes.string,
  children: PropTypes.node.isRequired,
  confirmText: PropTypes.string,
  icon: PropTypes.oneOf(["exclamation", "question"]),
  magnitude: PropTypes.oneOf(["medium", "small"]),
  minWidth: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSecondary: PropTypes.func,
  secondaryText: PropTypes.string,
  showCancelButton: PropTypes.bool,
  showSecondaryButton: PropTypes.bool,
  title: PropTypes.string,
  variant: PropTypes.oneOf(["action", "danger", "primary", "secondary"])
};
const defaultProps = {
  animation: true,
  "aria-label": void 0,
  cancelText: "Cancel",
  confirmText: "Delete",
  icon: "question",
  magnitude: "medium",
  minWidth: 320,
  onSecondary: void 0,
  secondaryText: void 0,
  showCancelButton: true,
  showSecondaryButton: false,
  title: void 0,
  variant: "action"
};
const ConfirmationDialog = ({
  animation,
  "aria-label": ariaLabel,
  cancelText,
  children,
  confirmText,
  icon,
  magnitude,
  minWidth,
  onCancel,
  onConfirm,
  onSecondary,
  secondaryText,
  showCancelButton,
  showSecondaryButton,
  title,
  variant
}) => {
  const id = useId();
  const buttonRef = useRef();
  return /* @__PURE__ */ jsx(
    Overlay,
    {
      animation,
      "aria-label": ariaLabel,
      "aria-labelledby": title && !ariaLabel ? `bungle-confirmation-dialog-${id}-header` : void 0,
      initialFocusRef: variant !== "danger" || showCancelButton ? buttonRef : void 0,
      minWidth,
      onClose: onCancel,
      children: /* @__PURE__ */ jsx(Dialog, { appearance: "round", children: /* @__PURE__ */ jsxs(ConfirmationDialogInner, { children: [
        /* @__PURE__ */ jsx(ConfirmationDialogIcon, { children: /* @__PURE__ */ jsx(Icon, { type: `${icon}-circle`, magnitude: "medium" }) }),
        title && /* @__PURE__ */ jsx(
          ConfirmationDialogHeader,
          {
            id: `bungle-confirmation-dialog-${id}-header`,
            children: title
          }
        ),
        /* @__PURE__ */ jsx(ConfirmationDialogBody, { children }),
        /* @__PURE__ */ jsx(ConfirmationDialogFooter, { children: /* @__PURE__ */ jsxs(Inline, { space: "small", alignX: "right", children: [
          showCancelButton && /* @__PURE__ */ jsx(
            Button,
            {
              ref: variant === "danger" ? buttonRef : void 0,
              magnitude,
              variant: "secondary",
              onClick: onCancel,
              children: cancelText
            }
          ),
          showSecondaryButton && onSecondary && /* @__PURE__ */ jsx(
            Button,
            {
              magnitude,
              variant: "secondary",
              onClick: onSecondary,
              children: secondaryText
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              magnitude,
              onClick: onConfirm,
              ref: variant !== "danger" ? buttonRef : void 0,
              variant,
              children: confirmText
            }
          )
        ] }) })
      ] }) })
    }
  );
};
ConfirmationDialog.propTypes = propTypes;
ConfirmationDialog.defaultProps = defaultProps;
export {
  ConfirmationDialog
};
